import { VDBText } from "@components/atoms";
import React, { useRef } from "react";
import { Platform, Text } from "react-native";
import type { NativeSyntheticEvent, TextLayoutEventData } from "react-native";
import styled from "styled-components/native";

export const PRICING_COMMENT_PREVIEW_LINES = 3;

type PricingCommentTextProps = {
  comment: string;
  color: string;
  typographyStyle: any;
  maxLines?: number;
  onLineCountChange?: (lineCount: number) => void;
};

const PricingCommentText = ({
  comment,
  color,
  typographyStyle,
  maxLines,
  onLineCountChange,
}: PricingCommentTextProps) => {
  const onLineCountChangeRef = useRef(onLineCountChange);
  const fullHeightRef = useRef(0);
  const lineHeightRef = useRef(0);
  onLineCountChangeRef.current = onLineCountChange;

  const publishLineCount = (lineCount: number) => {
    if (lineCount > 0) {
      onLineCountChangeRef.current?.(lineCount);
    }
  };

  const publishWebLineCount = () => {
    const fullHeight = fullHeightRef.current;
    const lineHeight = lineHeightRef.current;
    if (fullHeight <= 0 || lineHeight <= 0) return;
    publishLineCount(Math.max(1, Math.round(fullHeight / lineHeight)));
  };

  const measureTextStyle =
    Platform.OS === "web"
      ? [
          typographyStyle,
          {
            display: "block",
            width: "100%",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          },
        ]
      : [typographyStyle, { width: "100%", textAlign: "left" }];

  return (
    <CommentTextContainer>
      <TextBlock>
        <VDBText
          type={typographyStyle}
          color={color}
          textAlign="left"
          numberOfLines={maxLines}
          ellipsizeMode="tail"
        >
          {comment}
        </VDBText>
      </TextBlock>
      <MeasureContainer
        collapsable={false}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Text
          allowFontScaling={false}
          accessible={false}
          style={measureTextStyle}
          onTextLayout={(event: NativeSyntheticEvent<TextLayoutEventData>) => {
            if (Platform.OS === "web") return;
            publishLineCount(event.nativeEvent.lines.length);
          }}
          onLayout={(event) => {
            if (Platform.OS !== "web") return;
            const { height, width } = event.nativeEvent.layout;
            if (width <= 0 || height <= 0) return;
            fullHeightRef.current = height;
            publishWebLineCount();
          }}
        >
          {comment}
        </Text>
        <Text
          allowFontScaling={false}
          accessible={false}
          numberOfLines={1}
          style={measureTextStyle}
          onLayout={(event) => {
            if (Platform.OS !== "web") return;
            const { height } = event.nativeEvent.layout;
            if (height <= 0) return;
            lineHeightRef.current = height;
            publishWebLineCount();
          }}
        >
          Mg
        </Text>
      </MeasureContainer>
    </CommentTextContainer>
  );
};

const CommentTextContainer = styled.View`
  flex: 1;
  min-width: 0;
  width: 100%;
  position: relative;
  align-items: flex-start;
  overflow: hidden;
`;

const TextBlock = styled.View`
  width: 100%;
  align-items: flex-start;
`;

const MeasureContainer = styled.View`
  position: absolute;
  opacity: 0;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  z-index: -1;
`;

export default PricingCommentText;
